/**
 * Contrats — the VEOS endpoints of the contract list and the contract detail
 * screen (contract §6; fiches 22087 / 22089 / 22803 / 22805 / 22702 / 22717
 * / 22719 / 22721 / 22723).
 *
 * | function                  | endpoint slug                      | caller params           |
 * |---------------------------|------------------------------------|-------------------------|
 * | `fetchContracts`          | `boby-ws2-ea-tab-polices`          | `etat`?, `q`?           |
 * | `fetchStatusOptions`      | `boby-ws2-ea-sel-statut-contrat`   | —                       |
 * | `fetchContract`           | `boby-ws2-ea-fic-pol`              | `id_npol`               |
 * | `fetchInsured`            | `boby-ws2-d-fic-ass`               | `id_npol`, `id_per_ass` |
 * | `fetchContractPremiums`   | `boby-ws2-ea-tab-qui-pol`          | `id_npol`               |
 * | `fetchContractGuarantees` | `boby-ws2-ea-tab-gar-pol`          | `ID_NPOL`               |
 * | `fetchContractClaims`     | `boby-ws2-ea-tab-pol-sin`          | `ID_NPOL`               |
 * | `fetchContractPieces`     | `boby-ws2-ea-tab-pol-pmq`          | `ID_NPOL`               |
 * | `fetchContractDocuments`  | `boby-ws2-ea-tab-docs-pol`         | `ID_NPOL`               |
 *
 * Parameter casing is exactly the fiche's (VEOS is case-sensitive). `SES` is
 * appended by the proxy and `perPage` defaults to `9999` server-side.
 *
 * Every raw row is typed and mapped to a view model the cards consume; the
 * view models keep their `raw` row (or, for the list, ARE the raw row plus
 * camelCase aliases) so a DataList `fieldMapping` can still reach any VEOS
 * field. Null-safe: a `null` / blank VEOS value becomes `null` (« ne rien
 * afficher »), never the text "null".
 */

import type { ClaimTableRow } from '../components/shared/claimsTableConfig';
import type { DocumentItem } from '../components/shared/documentItems';
import type { GuaranteeTableRow } from '../components/shared/guaranteesTableConfig';
import { buildPieceUploadTarget, type PieceItem } from '../components/shared/pieceItems';
import {
  buildAddress,
  formatMsDate,
  formatSiret,
  formatText,
  toFiniteNumber,
  type VeosScalar,
} from '../utils/format';
import { erpEndpointPath, fetchErpList, fetchErpRecord, fetchErpRows, type ErpList } from './erpProxy';
import {
  buildFilterParams,
  fetchSelOptions,
  mapSelOptions,
  type SelOption,
  type SelOptionRow,
} from './listFilters';

// ---------------------------------------------------------------------------
// Endpoint slugs (contract §6)
// ---------------------------------------------------------------------------

export const CONTRACTS_ENDPOINTS = {
  list: 'boby-ws2-ea-tab-polices',
  statusOptions: 'boby-ws2-ea-sel-statut-contrat',
  detail: 'boby-ws2-ea-fic-pol',
  insured: 'boby-ws2-d-fic-ass',
  premiums: 'boby-ws2-ea-tab-qui-pol',
  guarantees: 'boby-ws2-ea-tab-gar-pol',
  claims: 'boby-ws2-ea-tab-pol-sin',
  pieces: 'boby-ws2-ea-tab-pol-pmq',
  documents: 'boby-ws2-ea-tab-docs-pol',
} as const;

/** Proxy URL of the contract list (DataList endpoint mode). */
export function contractsListEndpointPath(registrySlug: string): string {
  return erpEndpointPath(registrySlug, CONTRACTS_ENDPOINTS.list);
}

/** Proxy URL of the contract status options. */
export function contractStatusOptionsEndpointPath(registrySlug: string): string {
  return erpEndpointPath(registrySlug, CONTRACTS_ENDPOINTS.statusOptions);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Trimmed text, `null` when missing / blank. */
function textOrNull(value: VeosScalar): string | null {
  const text = formatText(value);
  return text === '' ? null : text;
}

/** A VEOS id as a string, `null` when missing / blank. */
function idOrNull(value: VeosScalar): string | null {
  return textOrNull(value);
}

/** Row id used when VEOS returned no id (`<prefix>-<index>`). */
function fallbackId(prefix: string, index: number): string {
  return `${prefix}-${String(index)}`;
}

// ---------------------------------------------------------------------------
// WS2_EA_TAB_POLICES — liste des contrats (fiches 22087 / 22089)
// ---------------------------------------------------------------------------

/** One raw `WS2_EA_TAB_POLICES` row (fiche 22087). */
export interface ContractListRow {
  ID_PER: number | string | null;
  NOM_PER: string | null;
  PRENOM_PER: string | null;
  /** Insured display name (trailing spaces in VEOS). */
  NOM2_PER: string | null;
  ID_NPOL: number | string | null;
  ID_POL: number | string | null;
  NUM_POL: string | null;
  CD_FBRA: string | null;
  LIB_FBRA: string | null;
  LIB_PROD: string | null;
  CD_BR: string | null;
  NOM_CIE: string | null;
  CD_POS_CODE: string | null;
  /** `EN COURS` | `RESILIE` — drives the status dot. Lower-case key in VEOS. */
  lib_situation: string | null;
  /** Effective date, pre-formatted `DD/MM/YYYY`. */
  DTEFF_POL: string | null;
  DTFIN_POL: string | null;
  ECHP_POL: string | null;
  TTC_POL: number | string | null;
  TYP_PROD: string | null;
  LIB_MOT: string | null;
}

/** List filters (fiche 22089): both omitted from the request when empty. */
export interface ContractsListFilters {
  /** `EC` | `RESILIE`; `''` / `null` / omitted ⇒ « Tous les statuts ». */
  etat?: string | null;
  /** Free-text search; blank ⇒ not sent. */
  q?: string | null;
}

/**
 * A contract of the list: the raw VEOS row (so `fieldMapping` reaches every
 * field) plus camelCase display aliases.
 */
export interface ContractSummary extends ContractListRow {
  /** Stable row id (`ID_NPOL`). */
  id: string;
  /** Insured person id (`ID_PER`) — the `id_per` of the detail route. */
  idPer: string | null;
  assure: string | null;
  produit: string | null;
  numero: string | null;
  assureur: string | null;
  dateEffet: string | null;
  statut: string | null;
}

/** Request params of the list: `etat` / `q` only when non-blank (trimmed). */
export function buildContractsListParams(
  filters: ContractsListFilters = {},
): Record<string, string> {
  return buildFilterParams({ etat: filters.etat, q: filters.q });
}

/** Map one `WS2_EA_TAB_POLICES` row. */
export function mapContractSummary(row: ContractListRow, index = 0): ContractSummary {
  return {
    ...row,
    id: idOrNull(row.ID_NPOL) ?? fallbackId('npol', index),
    idPer: idOrNull(row.ID_PER),
    assure: textOrNull(row.NOM2_PER),
    produit: textOrNull(row.LIB_PROD),
    numero: textOrNull(row.NUM_POL),
    assureur: textOrNull(row.NOM_CIE),
    dateEffet: textOrNull(row.DTEFF_POL),
    statut: textOrNull(row.lib_situation),
  };
}

// ---------------------------------------------------------------------------
// WS2_EA_SEL_STATUT_CONTRAT — options du filtre « Statut » (fiche 22089)
// ---------------------------------------------------------------------------

/** One raw `WS2_EA_SEL_STATUT_CONTRAT` row (shared `WS2_EA_SEL_*` shape). */
export type ContractStatusOptionRow = SelOptionRow;

/** A status option (`VAL` → `etat`, `LIB` → label). */
export type ContractStatusOption = SelOption;

/** Map the status rows, dropping the ones without a value (shared {@link mapSelOptions}). */
export function mapContractStatusOptions(
  rows: readonly ContractStatusOptionRow[],
): ContractStatusOption[] {
  return mapSelOptions(rows);
}

// ---------------------------------------------------------------------------
// WS2_EA_FIC_POL — détail du contrat (fiche 22803)
// ---------------------------------------------------------------------------

/**
 * The `WS2_EA_FIC_POL` fields the screen uses (the record carries ~80 more
 * construction-risk fields, kept in `raw`).
 */
export interface ContractDetailRow {
  NUM_POL: string | null;
  LIB_PROD: string | null;
  CD_PROD: string | null;
  NOM_CIE: string | null;
  /** Effective date, pre-formatted `JJ/MM/AAAA`. */
  DTEFF_POL: string | null;
  /** Position label, e.g. `EN COURS`. */
  CD_POS: string | null;
  /** Due date, pre-formatted `JJ/MM/AAAA`. */
  DTFIN_POL: string | null;
  LIB_BR?: string | null;
  CD_FRAC?: string | null;
  LIB_FRAC: string | null;
  TTC_POL: number | string | null;
  [field: string]: unknown;
}

/** The contract record of the detail screen. */
export interface ContractDetail {
  numero: string | null;
  produit: string | null;
  codeProduit: string | null;
  assureur: string | null;
  dateEffet: string | null;
  dateEcheance: string | null;
  /** Annual premium in euros (`TTC_POL`). */
  primeAnnuelle: number | null;
  fractionnement: string | null;
  /** Position (`CD_POS`, e.g. `EN COURS`). */
  statut: string | null;
  raw: ContractDetailRow;
}

/** Map the `WS2_EA_FIC_POL` record. */
export function mapContractDetail(row: ContractDetailRow): ContractDetail {
  return {
    numero: textOrNull(row.NUM_POL),
    produit: textOrNull(row.LIB_PROD),
    codeProduit: textOrNull(row.CD_PROD),
    assureur: textOrNull(row.NOM_CIE),
    dateEffet: textOrNull(row.DTEFF_POL),
    dateEcheance: textOrNull(row.DTFIN_POL),
    primeAnnuelle: toFiniteNumber(row.TTC_POL),
    fractionnement: textOrNull(row.LIB_FRAC),
    statut: textOrNull(row.CD_POS),
    raw: row,
  };
}

// ---------------------------------------------------------------------------
// WS2_D_FIC_ASS — assuré du contrat (fiche 22805)
// ---------------------------------------------------------------------------

/** The `WS2_D_FIC_ASS` fields the screen uses (the rest is kept in `raw`). */
export interface InsuredRow {
  NOM2_PER: string | null;
  ADRESSE1_ADR: string | null;
  ADRESSE2_ADR: string | null;
  ADRESSE3_ADR: string | null;
  VILLE_ADR: string | null;
  CD_PST: string | number | null;
  EMAIL_PER: string | null;
  TELPRINC_PER: string | null;
  SIREN_PMO: string | number | null;
  NIC: string | number | null;
  [field: string]: unknown;
}

/** The insured person of a contract, display-ready. */
export interface ContractInsured {
  nom: string | null;
  /** `812 456 789 00021` (SIREN_PMO + NIC). */
  siret: string | null;
  /** `adresse, CP Ville` — null parts skipped. */
  adresse: string | null;
  email: string | null;
  telephone: string | null;
  raw: InsuredRow;
}

/** Map the `WS2_D_FIC_ASS` record. */
export function mapInsured(row: InsuredRow): ContractInsured {
  const siret = formatSiret(row.SIREN_PMO, row.NIC);
  const adresse = buildAddress({
    lines: [row.ADRESSE1_ADR, row.ADRESSE2_ADR, row.ADRESSE3_ADR],
    postalCode: row.CD_PST,
    city: row.VILLE_ADR,
  });
  return {
    nom: textOrNull(row.NOM2_PER),
    siret: siret === '' ? null : siret,
    adresse: adresse === '' ? null : adresse,
    email: textOrNull(row.EMAIL_PER),
    telephone: textOrNull(row.TELPRINC_PER),
    raw: row,
  };
}

// ---------------------------------------------------------------------------
// WS2_EA_TAB_QUI_POL — primes du contrat (fiche 22702)
// ---------------------------------------------------------------------------

/** One raw `WS2_EA_TAB_QUI_POL` row. */
export interface ContractPremiumRow {
  ID_POL: number | string | null;
  NUM_QUI: string | null;
  NUM_POL: string | null;
  NOM2_PER: string | null;
  NOM_CIE: string | null;
  TTC_QUI: number | string | null;
  /** Period start — ms timestamp. */
  DATDEB_QUI: number | string | null;
  /** Period end — ms timestamp. */
  DTFIN_QUI: number | string | null;
  PERIODE: string | null;
  DATEMI_QUI: number | string | null;
  /** « Payée … » / « Impayée … ». */
  QUI_SITUATION: string | null;
  LIB_SQ: string | null;
  DATREG_QUI: number | string | null;
  LIB_PROD: string | null;
  ID_PER: number | string | null;
  ID_NPOL: number | string | null;
  ID_QUI: number | string | null;
}

/** A premium row of the contract (« Primes » tab). */
export interface ContractPremium {
  /** Stable row id (`ID_QUI`). */
  id: string;
  /** Premium reference (`NUM_QUI`). */
  reference: string | null;
  /** Period start, ms timestamp (`DATDEB_QUI`) — rendered `JJ/MM/AAAA`. */
  periodeDu: number | null;
  /** Period end, ms timestamp (`DTFIN_QUI`). */
  periodeAu: number | null;
  /** Amount incl. taxes in euros (`TTC_QUI`). */
  ttc: number | null;
  /** Raw situation (`QUI_SITUATION`), shown verbatim in its badge. */
  situation: string | null;
}

/** Map one `WS2_EA_TAB_QUI_POL` row. */
export function mapContractPremium(row: ContractPremiumRow, index = 0): ContractPremium {
  return {
    id: idOrNull(row.ID_QUI) ?? fallbackId('qui', index),
    reference: textOrNull(row.NUM_QUI),
    periodeDu: toFiniteNumber(row.DATDEB_QUI),
    periodeAu: toFiniteNumber(row.DTFIN_QUI),
    ttc: toFiniteNumber(row.TTC_QUI),
    situation: textOrNull(row.QUI_SITUATION),
  };
}

// ---------------------------------------------------------------------------
// WS2_EA_TAB_GAR_POL — garanties du contrat (fiche 22717)
// ---------------------------------------------------------------------------

/** One raw `WS2_EA_TAB_GAR_POL` row. */
export interface ContractGuaranteeRow {
  ID_NPOL: number | string | null;
  ID_POL: number | string | null;
  NUM_POL: string | null;
  TYPE: string | null;
  CD_GAR: string | null;
  LIB_GAR: string | null;
  CD_TYFR: string | null;
  LIB_TYFR: string | null;
  /** Deductible (FRA_GARP; `null` when 0). */
  FRANCHISE: number | string | null;
  CD_TYCAP: string | null;
  LIB_TYCAP: string | null;
  /** Ceiling (`null` when 0). */
  CAP_GARP: number | string | null;
  LIB_FORM: string | null;
}

/** Map the guarantee rows (API order); ids are `CD_GAR`, made unique. */
export function mapContractGuarantees(
  rows: readonly ContractGuaranteeRow[],
): GuaranteeTableRow[] {
  const seen = new Set<string>();
  return rows.map((row, index) => {
    const code = idOrNull(row.CD_GAR);
    let id = code ?? fallbackId('gar', index);
    if (seen.has(id)) id = fallbackId(id, index);
    seen.add(id);
    return {
      id,
      libelle: textOrNull(row.LIB_GAR),
      plafond: toFiniteNumber(row.CAP_GARP),
      franchise: toFiniteNumber(row.FRANCHISE),
    };
  });
}

// ---------------------------------------------------------------------------
// WS2_EA_TAB_POL_SIN — sinistres du contrat (fiche 22719)
// ---------------------------------------------------------------------------

/** One raw `WS2_EA_TAB_POL_SIN` row. */
export interface ContractClaimRow {
  ID_POL: number | string | null;
  ID_PER: number | string | null;
  ID_NPOL: number | string | null;
  NUM_POL: string | null;
  LIB_BR: string | null;
  ID_SIN: number | string | null;
  NCBT_SIN: string | null;
  LIB_MVT: string | null;
  LIB_MOT: string | null;
  LIB_CIRC: string | null;
  /** Occurrence date — ms timestamp. */
  DATS_SIN: number | string | null;
  ETAT: string | null;
  LIB_ETAT: string | null;
  TAUX_RESP: string | null;
  COUT_TOTAL_ASSUREUR: number | string | null;
  NOM2_PER: string | null;
  /** Estimated insurer cost — displayed (« Coût »). */
  COUT_ESTIME: number | string | null;
}

/**
 * Map one `WS2_EA_TAB_POL_SIN` row (API order = date de survenance
 * décroissante). `to` (the claim detail path) is set by the page, which
 * knows the tenant.
 */
export function mapContractClaim(row: ContractClaimRow, index = 0): ClaimTableRow {
  return {
    id: idOrNull(row.ID_SIN) ?? fallbackId('sin', index),
    reference: textOrNull(row.NCBT_SIN),
    circonstances: textOrNull(row.LIB_CIRC),
    dateSurvenance: toFiniteNumber(row.DATS_SIN),
    coutAssureur: toFiniteNumber(row.COUT_ESTIME),
  };
}

// ---------------------------------------------------------------------------
// WS2_EA_TAB_POL_PMQ — pièces du contrat (fiche 22721)
// ---------------------------------------------------------------------------

/** One raw `WS2_EA_TAB_POL_PMQ` row. */
export interface ContractPieceRow {
  ID_POL: number | string | null;
  NUM_POL: string | null;
  ID_NPOL: number | string | null;
  NOMCOMPLETASS: string | null;
  LIB_PIE: string | null;
  /** Request date — ms timestamp. */
  DATDEMANDE_PMD: number | string | null;
  CD_PIE: string | null;
  ID_PMD: number | string | null;
  /** e.g. `EN ATTENTE`. */
  STATUT: string | null;
  /** Document category of the piece — the upload `type` (fiches 22721 / 22975). */
  CD_DS?: string | null;
}

/** Raw status of a piece still awaited (« Pièces (n) » counter). */
export const PENDING_PIECE_STATUS = 'EN ATTENTE';

/**
 * Map one `WS2_EA_TAB_POL_PMQ` row. `upload` carries the ids of the piece
 * upload (fiche 22975): `idTab` = `ID_POL`, call 2 path `ID_NPOL` / `ID_PMD`,
 * `type` = `CD_DS` — `null` when one is missing.
 */
export function mapContractPiece(row: ContractPieceRow, index = 0): PieceItem {
  const dateDemande = formatMsDate(row.DATDEMANDE_PMD);
  return {
    id: idOrNull(row.ID_PMD) ?? fallbackId('pmd', index),
    libelle: textOrNull(row.LIB_PIE),
    dateDemande: dateDemande === '' ? null : dateDemande,
    statut: textOrNull(row.STATUT),
    upload: buildPieceUploadTarget('contrat', {
      idPmd: toFiniteNumber(row.ID_PMD),
      idTab: toFiniteNumber(row.ID_POL),
      parentId: toFiniteNumber(row.ID_NPOL),
      cdDs: textOrNull(row.CD_DS),
    }),
  };
}

/** Number of pieces whose `STATUT` is « EN ATTENTE » (trim / case tolerant). */
export function countPendingPieces(pieces: readonly PieceItem[]): number {
  return pieces.filter((piece) => piece.statut?.trim().toUpperCase() === PENDING_PIECE_STATUS)
    .length;
}

// ---------------------------------------------------------------------------
// WS2_EA_TAB_DOCS_POL — documents du contrat (fiche 22723)
// ---------------------------------------------------------------------------

/** One raw `WS2_EA_TAB_DOCS_POL` row. */
export interface ContractDocumentRow {
  LIB_ADOC: string | null;
  /** Document date — ms timestamp. */
  DAT_ADOC: number | string | null;
  ID_ADOC: number | string | null;
  /** Category; `null` ⇒ unlabelled group. */
  LIB_DS: string | null;
  TYPE: string | null;
}

/** Map one `WS2_EA_TAB_DOCS_POL` row. */
export function mapContractDocument(row: ContractDocumentRow, index = 0): DocumentItem {
  const date = formatMsDate(row.DAT_ADOC);
  return {
    id: idOrNull(row.ID_ADOC) ?? fallbackId('adoc', index),
    categorie: textOrNull(row.LIB_DS),
    nom: textOrNull(row.LIB_ADOC),
    date: date === '' ? null : date,
  };
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

/** React Query keys of the contract screens. */
export const contractQueryKeys = {
  all: ['extranet', 'contracts'] as const,
  detail: (registrySlug: string | null, idNpol: string, part: string) =>
    [...contractQueryKeys.all, registrySlug, idNpol, part] as const,
};

type RegistrySlug = string | null | undefined;

/** Contract list (`WS2_EA_TAB_POLICES`), filtered by `etat` / `q`. */
export async function fetchContracts(
  registrySlug: RegistrySlug,
  filters: ContractsListFilters = {},
): Promise<ErpList<ContractSummary>> {
  const list = await fetchErpList<ContractListRow>(
    registrySlug,
    CONTRACTS_ENDPOINTS.list,
    buildContractsListParams(filters),
  );
  return { rows: list.rows.map(mapContractSummary), total: list.total };
}

/** Options of the « Statut » filter (`WS2_EA_SEL_STATUT_CONTRAT`), API order. */
export async function fetchStatusOptions(
  registrySlug: RegistrySlug,
): Promise<ContractStatusOption[]> {
  return fetchSelOptions(registrySlug, CONTRACTS_ENDPOINTS.statusOptions);
}

/** Contract record (`WS2_EA_FIC_POL`); `null` when VEOS returned none. */
export async function fetchContract(
  registrySlug: RegistrySlug,
  idNpol: string,
): Promise<ContractDetail | null> {
  const row = await fetchErpRecord<ContractDetailRow>(registrySlug, CONTRACTS_ENDPOINTS.detail, {
    id_npol: idNpol,
  });
  return row ? mapContractDetail(row) : null;
}

/** Insured person (`WS2_D_FIC_ASS`); `null` when VEOS returned none. */
export async function fetchInsured(
  registrySlug: RegistrySlug,
  idNpol: string,
  idPer: string,
): Promise<ContractInsured | null> {
  const row = await fetchErpRecord<InsuredRow>(registrySlug, CONTRACTS_ENDPOINTS.insured, {
    id_npol: idNpol,
    id_per_ass: idPer,
  });
  return row ? mapInsured(row) : null;
}

/** Premiums of the contract (`WS2_EA_TAB_QUI_POL`). */
export async function fetchContractPremiums(
  registrySlug: RegistrySlug,
  idNpol: string,
): Promise<ContractPremium[]> {
  const rows = await fetchErpRows<ContractPremiumRow>(registrySlug, CONTRACTS_ENDPOINTS.premiums, {
    id_npol: idNpol,
  });
  return rows.map(mapContractPremium);
}

/** Guarantees of the contract (`WS2_EA_TAB_GAR_POL`). */
export async function fetchContractGuarantees(
  registrySlug: RegistrySlug,
  idNpol: string,
): Promise<GuaranteeTableRow[]> {
  const rows = await fetchErpRows<ContractGuaranteeRow>(
    registrySlug,
    CONTRACTS_ENDPOINTS.guarantees,
    { ID_NPOL: idNpol },
  );
  return mapContractGuarantees(rows);
}

/** Claims of the contract (`WS2_EA_TAB_POL_SIN`), API order. */
export async function fetchContractClaims(
  registrySlug: RegistrySlug,
  idNpol: string,
): Promise<ClaimTableRow[]> {
  const rows = await fetchErpRows<ContractClaimRow>(registrySlug, CONTRACTS_ENDPOINTS.claims, {
    ID_NPOL: idNpol,
  });
  return rows.map(mapContractClaim);
}

/** Requested pieces of the contract (`WS2_EA_TAB_POL_PMQ`). */
export async function fetchContractPieces(
  registrySlug: RegistrySlug,
  idNpol: string,
): Promise<PieceItem[]> {
  const rows = await fetchErpRows<ContractPieceRow>(registrySlug, CONTRACTS_ENDPOINTS.pieces, {
    ID_NPOL: idNpol,
  });
  return rows.map(mapContractPiece);
}

/** Documents of the contract (`WS2_EA_TAB_DOCS_POL`), API order. */
export async function fetchContractDocuments(
  registrySlug: RegistrySlug,
  idNpol: string,
): Promise<DocumentItem[]> {
  const rows = await fetchErpRows<ContractDocumentRow>(
    registrySlug,
    CONTRACTS_ENDPOINTS.documents,
    { ID_NPOL: idNpol },
  );
  return rows.map(mapContractDocument);
}

/** The service as one object (mock-friendly in page tests). */
export const contractsService = {
  fetchContracts,
  fetchStatusOptions,
  fetchContract,
  fetchInsured,
  fetchContractPremiums,
  fetchContractGuarantees,
  fetchContractClaims,
  fetchContractPieces,
  fetchContractDocuments,
};
